/**
 * Geocoding service using Google Maps Geocoding API
 * Uses the same API key as the rest of the Google Maps integration
 */

import { config } from '../config/config';
import { ensureGoogleMapsLoaded } from '../utils/googleMapsLoader';

export interface GeocodingResult {
  lat: number;
  lng: number;
  address: string;
}

export interface ReverseGeocodingResult {
  /** Raw postal code Google reports for the point, e.g. "34761" or "M5V 2T6". */
  postalCode: string | null;
  /** Human label for the spot, e.g. "Ocoee, FL". Null when Google can't name it. */
  place: string | null;
}

export class GeocodingService {
  private static cache = new Map<string, GeocodingResult>();
  private static reverseCache = new Map<string, ReverseGeocodingResult>();

  /**
   * Geocode an address to get lat/lng coordinates using Google Maps
   */
  static async geocodeAddress(address: string): Promise<GeocodingResult | null> {
    if (!address || address.trim() === '') {
      return null;
    }

    const cacheKey = address.toLowerCase().trim();

    // Check cache first
    if (this.cache.has(cacheKey)) {
      console.log('🗺️ Using cached geocoding result for:', address);
      return this.cache.get(cacheKey)!;
    }

    // Self-loads the Maps script if this is called from a page that never
    // mounted an address input (e.g. Routy opened from Settings/Team Members).
    if (!window.google?.maps?.Geocoder) {
      try {
        await ensureGoogleMapsLoaded();
      } catch (err) {
        console.warn('⚠️ Google Maps failed to load, falling back to address without coordinates:', err);
        return null;
      }
    }
    if (!window.google?.maps?.Geocoder) {
      console.warn('⚠️ Google Maps Geocoder not available, falling back to address without coordinates');
      return null;
    }

    try {
      console.log('🌍 Geocoding address with Google Maps:', address);
      
      const geocoder = new google.maps.Geocoder();
      const results = await new Promise<google.maps.GeocoderResult[] | null>((resolve) => {
        geocoder.geocode({ address }, (geocodeResults, status) => {
          if (status === google.maps.GeocoderStatus.OK && geocodeResults?.length) {
            resolve(geocodeResults);
            return;
          }
          resolve(null);
        });
      });

      if (results && results.length > 0) {
        const location = results[0].geometry.location;
        const geocodingResult: GeocodingResult = {
          lat: location.lat(),
          lng: location.lng(),
          address: results[0].formatted_address || address
        };

        console.log('✅ Geocoded successfully:', geocodingResult);

        // Cache the result
        this.cache.set(cacheKey, geocodingResult);

        return geocodingResult;
      }

      console.warn('⚠️ No geocoding results found for:', address);
      return null;
    } catch (error) {
      console.error('❌ Geocoding error:', error);
      return null;
    }
  }

  /**
   * Reverse geocode a coordinate to the postal code and city/state that
   * contain it. Returns null when Google is unavailable or has no result
   * for the point (e.g. open water).
   */
  static async reverseGeocode(lat: number, lng: number): Promise<ReverseGeocodingResult | null> {
    // Google's postal areas are far coarser than 4 decimal places, so rounding
    // the key lets nearby clicks share one lookup.
    const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
    const cached = this.reverseCache.get(cacheKey);
    if (cached) return cached;

    if (!window.google?.maps?.Geocoder) {
      try {
        await ensureGoogleMapsLoaded();
      } catch (err) {
        console.warn('⚠️ Google Maps failed to load, cannot reverse geocode:', err);
        return null;
      }
    }
    if (!window.google?.maps?.Geocoder) return null;

    try {
      const geocoder = new google.maps.Geocoder();
      const results = await new Promise<google.maps.GeocoderResult[] | null>((resolve) => {
        geocoder.geocode({ location: { lat, lng } }, (geocodeResults, status) => {
          resolve(status === google.maps.GeocoderStatus.OK && geocodeResults?.length ? geocodeResults : null);
        });
      });
      if (!results) return null;

      const component = (type: string, short = false): string | null => {
        for (const result of results) {
          const match = result.address_components?.find(c => c.types.includes(type));
          if (match) return short ? match.short_name : match.long_name;
        }
        return null;
      };

      const city = component('locality') ?? component('sublocality') ?? component('administrative_area_level_3');
      const state = component('administrative_area_level_1', true);
      const result: ReverseGeocodingResult = {
        postalCode: component('postal_code'),
        place: [city, state].filter(Boolean).join(', ') || null,
      };
      this.reverseCache.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error('❌ Reverse geocoding error:', error);
      return null;
    }
  }

  /**
   * Batch geocode multiple addresses
   */
  static async geocodeAddresses(addresses: string[]): Promise<Map<string, GeocodingResult>> {
    const results = new Map<string, GeocodingResult>();
    
    // Process addresses in parallel with a small delay between each to avoid rate limits
    for (const address of addresses) {
      const result = await this.geocodeAddress(address);
      if (result) {
        results.set(address, result);
      }
      // Small delay to avoid hitting rate limits (Google allows 50 requests per second)
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    return results;
  }

  /**
   * Clear the geocoding cache
   */
  static clearCache(): void {
    this.cache.clear();
    this.reverseCache.clear();
  }
}